import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import { splitByActive } from "../lib/activeList.js";
import { Icon } from "../components/Icon.js";
import { Sheet } from "../components/Sheet.js";
import type { EnrolCode, ManagedDevice } from "../types.js";
import { SHOP_ZONE } from "../lib/berlin.js";

/**
 * The phones and tablets, and how one gets registered.
 *
 * Admin only, and the server says the same on every route behind it —
 * a device token opens everything the app can do, so this is not a
 * hidden button pretending to be a restriction.
 *
 * **Two ways to enrol, and the QR is the one to use.** It exists because
 * thirty phones would otherwise need 43 characters typed into each, and
 * because the token path has no answer for a phone that is replaced.
 * The token is still shown once on creation, for the case where a camera
 * is not an option.
 *
 * **What the QR carries is not the token.** A device token lasts two
 * years and cannot be read back, so as a QR it would be copyable by
 * anyone who photographs this screen — with no way afterwards to tell
 * which one leaked or that one did. It carries a code that names the
 * device, is signed, expires in ten minutes and works once. ROADMAP has the
 * argument under "Level 1 — QR enrolment".
 *
 * Nothing here deletes. A lost phone is revoked, which leaves the row.
 */
/**
 * The session lengths worth offering, and what each is for.
 *
 * Per device rather than per person on purpose: the same employee should
 * get a short session on a tablet anyone can pick up and a long one on
 * the phone in their pocket. That is the whole reason the column lives
 * on Device.
 */
const PRESETS = [30, 480, 43_200] as const;

function describe(minutes: number): string {
  if (minutes === 30) return "30 minutes — a shared tablet";
  if (minutes === 480) return "8 hours — one shift";
  if (minutes === 43_200) return "30 days — someone's own phone";
  if (minutes < 60) return `${minutes} minutes`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} hours`;
  return `${Math.round(minutes / 1440)} days`;
}

/**
 * The presets plus whatever this device is already set to.
 *
 * Without the second half, opening the menu on a device set to something
 * unusual — anything set through the API rather than this menu — would
 * show a value it does not have, and picking anything would look like a
 * no-op while quietly changing it.
 */
/** m:ss once a code lives long enough for bare seconds to read oddly. */
function countdown(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function presetsFor(current: number): number[] {
  const all = [...PRESETS] as number[];
  return all.includes(current) ? all : [current, ...all];
}

export function DevicesPanel() {
  const [devices, setDevices] = useState<ManagedDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  // Shown once and never readable again, so it stays until dismissed
  // rather than fading. Same rule as a new PIN.
  const [issuedToken, setIssuedToken] = useState<{ name: string; token: string } | null>(
    null,
  );
  const [showing, setShowing] = useState<EnrolCode | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  // Separate from `copied`, which belongs to the enrol link. Both can be
  // on screen at once, and a button reading "Copied" because the *other*
  // one was pressed is worse feedback than none.
  const [tokenCopied, setTokenCopied] = useState(false);
  // Revoked devices are hidden until asked for. Revoking is one-way and
  // registering is cheap, so the list fills with rows nobody needs to see
  // again — the same problem the task list has, solved the same way.
  const [showRevoked, setShowRevoked] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  // Set once, when a length is changed, because the change does not take
  // effect until the next sign-in and an admin watching nothing happen
  // will otherwise assume it failed.
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { devices: list } = await api.adminDevices();
      setDevices(list);
    } catch {
      setError("Could not load the devices.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The code dies on the server whether or not this screen says so. A
  // QR that is silently a minute stale is a phone that scans and fails,
  // and the person holding it blames the phone.
  //
  // Counted down to a deadline, not by ticks. iOS freezes a backgrounded
  // tab, and a countdown that subtracted one per tick resumed where it
  // stopped — showing a code the server had expired while the tablet
  // slept. The clock is read on every tick, and again on coming back.
  useEffect(() => {
    if (!showing) return;
    setCopied(false);
    const deadline = Date.now() + showing.expiresInSeconds * 1000;
    const recount = () => {
      const left = Math.ceil((deadline - Date.now()) / 1000);
      if (left <= 0) {
        setShowing(null);
        setSecondsLeft(0);
      } else {
        setSecondsLeft(left);
      }
    };
    recount();
    const tick = setInterval(recount, 1000);
    document.addEventListener("visibilitychange", recount);
    return () => {
      clearInterval(tick);
      document.removeEventListener("visibilitychange", recount);
    };
  }, [showing]);

  const run = async (work: () => Promise<void>, whenFailed: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      const known: Record<string, string> = {
        device_revoked: "That device is revoked. Add a new one instead.",
        not_found: "That device is no longer there.",
        forbidden: "Only an admin can do that.",
      };
      setError(known[code] ?? whenFailed);
    } finally {
      setBusy(false);
    }
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    void run(async () => {
      const { device, token } = await api.addDevice(name.trim());
      setTokenCopied(false);
      setIssuedToken({ name: device.name, token });
      setName("");
    }, "Could not add the device.");
  };

  const { listed, hiddenCount: revokedCount } = splitByActive(devices, showRevoked);

  return (
    <>
      {/* Inside the sheet instead while one is open — behind the overlay
          an error is a failure nobody sees. */}
      {error && !showing ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p className="notice" role="status">{notice}</p> : null}

      {issuedToken ? (
        <section className="card issued" role="status">
          <p>
            Token for {issuedToken.name}. Shown once and never again — but
            you do not need it if you use the code below.
          </p>
          <code className="token">{issuedToken.token}</code>
          <div className="row-actions">
            {/* 43 characters, shown exactly once, and typing them by hand is
                how a character gets dropped into a config file that then
                fails with an indistinguishable 401. Same treatment as the
                enrol link: a button, with the text still selectable above
                it for when the clipboard is refused. */}
            <button
              type="button"
              className="ghost"
              onClick={() => {
                void navigator.clipboard
                  .writeText(issuedToken.token)
                  .then(() => setTokenCopied(true))
                  .catch(() =>
                    setError("Could not copy. Select the token above instead."),
                  );
              }}
            >
              {tokenCopied ? "Copied — paste it where it is needed" : "Copy the token"}
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="primary"
              onClick={() => setIssuedToken(null)}
            >
              Done
            </button>
          </div>
        </section>
      ) : null}

      <section className="card">
        <h2>Add a device</h2>
        <form onSubmit={add} className="field-action">
          <label>
            Device name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name it — whose phone, or which tablet"
              aria-label="Device name"
            />
          </label>
          <button type="submit" className="primary" disabled={busy || !name.trim()}>
            Add a device
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Devices</h2>
        {devices.length === 0 ? (
          <p className="muted">No devices yet.</p>
        ) : listed.length === 0 ? (
          <p className="muted">Every device is revoked.</p>
        ) : (
          <ul className="joblist">
            {listed.map((device) => (
              <li
                key={device.id}
                className={
                  !device.active ? "retired" : confirmRevoke === device.id ? "confirming" : ""
                }
              >
                <div className="item-text">
                  <span className="item-title">
                    <strong>{device.name}</strong>
                    {!device.active ? <span className="badge">revoked</span> : null}
                  </span>
                  <span className="item-meta">
                    {device.lastSeenAt
                      ? `last used ${new Date(device.lastSeenAt).toLocaleString("de-DE", { timeZone: SHOP_ZONE })}`
                      : "never used"}
                  </span>
                </div>
                {device.active ? (
                  <div className="device-controls">
                    {/* Presets rather than a number of minutes, because
                        the question is never "how many minutes" — it is
                        whether anyone can pick this device up. 43200 is
                        a month, and typing that into a box is a chance
                        to get a zero wrong. The device's current value
                        is always in the list even when it is not one of
                        these, so opening the menu cannot silently
                        change it. */}
                    <label className="session-length">
                      <span>Signed in for</span>
                      <select
                        value={device.sessionMinutes}
                        disabled={busy}
                        onChange={(e) => {
                          const minutes = Number(e.target.value);
                          void run(async () => {
                            await api.setDeviceSession(device.id, minutes);
                            setNotice(
                              `${device.name}: ${describe(minutes)}. ` +
                                `Takes effect the next time somebody signs in ` +
                                `on it — a session already open keeps the ` +
                                `length it was given.`,
                            );
                          }, "Could not change the session length.");
                        }}
                      >
                        {presetsFor(device.sessionMinutes).map((m) => (
                          <option key={m} value={m}>
                            {describe(m)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {confirmRevoke === device.id ? (
                      <>
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => setConfirmRevoke(null)}
                          disabled={busy}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="danger"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await api.revokeDevice(device.id);
                              setConfirmRevoke(null);
                            }, "Could not revoke it.")
                          }
                        >
                          Confirm
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              setShowing(await api.enrolCode(device.id));
                            }, "Could not make a code.")
                          }
                        >
                          <Icon name="qr" size={22} />
                          Show code
                        </button>
                        {/* Revoking signs the device out for good and there
                            is no un-revoke, so it takes a second tap. */}
                        <button
                          type="button"
                          className="caution"
                          disabled={busy}
                          onClick={() => setConfirmRevoke(device.id)}
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {/* The same switch as the task list, for the same reason: revoked
            rows accumulate and push the live ones off the screen. Counted
            rather than merely hidden, so nobody wonders where a device
            went. */}
        {revokedCount > 0 ? (
          <label className="list-toggle">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            Show revoked devices ({revokedCount})
          </label>
        ) : null}

        <p className="hint">
          Revoking a device signs that phone out and cannot be undone — add
          a new one instead. Nothing recorded from it is affected.
        </p>
      </section>

      {/* Over the list rather than inside it. A code is held up to a
          phone, so it has to be on screen whichever row was tapped — the
          reason it once moved from above the list into the row, and the
          reason it is now pinned to the viewport instead. */}
      {showing ? (
        <Sheet title={`Register ${showing.device.name}`} onClose={() => setShowing(null)}>
          {error ? <p className="error" role="alert">{error}</p> : null}
          {showing.isThisDevice ? (
            // Loudest thing on the sheet, and first, because this is the
            // one that already went wrong: an admin at a laptop made a
            // code for the device that laptop was enrolled as, scanned it
            // with a phone, and logged the laptop out with nothing to say
            // why.
            <p className="callout error">
              This is the device you are using right now. If anything
              scans this, {showing.device.name} moves to it and this
              browser is signed out — you would need a new token to get
              back in. To set up another device, use “Add a device”
              instead.
            </p>
          ) : null}
          <div className="qr-layout">
            {/* The SVG is built by this app's own server from a code it
                just minted — not user content, and not from anywhere else.
                The device name inside it is XML-escaped there. */}
            <div className="qr" dangerouslySetInnerHTML={{ __html: showing.svg }} />
            <div className="qr-side">
              <p className="qr-lead">
                Point the phone's camera at this. {showing.device.name}.
              </p>
              <div>
                <p className="muted">Expires in</p>
                <p className="countdown">{countdown(secondsLeft)}</p>
                <p className="muted">Generating another is free.</p>
              </div>
              {showing.inUse && !showing.isThisDevice ? (
                // Not "on a phone": a device in this list can be something
                // nobody is holding, and re-keying that is the case where
                // nothing visibly breaks. (It used to name the print agent,
                // which went with the print queue on 2026-09-24.)
                <p className="callout warn">
                  {showing.device.name} has been used before. Scanning this
                  moves it to whatever scans next, and whatever holds it now
                  stops working.
                </p>
              ) : null}
            </div>
          </div>

          {/* A laptop has no camera to point at another screen. The QR is
              only an encoding of this URL, so anything that can be pasted
              into an address bar works exactly as well — and the way that
              reaches another machine is a copy and a message, not
              somebody reading 120 characters aloud. Typing it was never
              realistic, which is why this is a button and not just text. */}
          <div className="link-out">
            <p className="label">For a device with no camera</p>
            <code className="token">{showing.url}</code>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                void navigator.clipboard
                  .writeText(showing.url)
                  .then(() => setCopied(true))
                  // Clipboard access can be refused, and an unlabelled
                  // failure here looks like the button doing nothing. The
                  // link is on screen either way.
                  .catch(() => setError("Could not copy. Select the link above instead."));
              }}
            >
              {copied ? "Copied — paste it where it is needed" : "Copy the link"}
            </button>
            <p className="hint">
              Anyone holding this link can register {showing.device.name} until
              it expires, so send it the way you would a password and delete
              it afterwards.
            </p>
          </div>
        </Sheet>
      ) : null}
    </>
  );
}
